import React, { useCallback, useRef, type ReactNode } from 'react';
import { v4 as uuid } from 'uuid';
import type { CommentEditorSourceTypes } from '@jira/issue__redux-state-types/src/IssueComment.types.tsx';
import type { DocNode as ADF } from '@atlaskit/adf-schema';
import {
	AIEventsInstrumentationProvider,
	useAIEventsInstrumentation,
} from '@atlassian/ai-analytics';

import AIContainer from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-container/index.tsx';
import {
	useIssueKey,
	useShouldUseRelayIssueComments,
} from '@atlassian/jira-issue-context-service/src/main.tsx';
import { DESCRIPTION, ASSIGNEE } from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import { connect } from '@atlassian/jira-issue-view-react-redux/src/index.tsx';
import { fieldSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/field-selector.tsx';
import {
	totalCommentsSelector,
	relayBridgeHasCommentsSelector,
} from '@atlassian/jira-issue-view-store/src/selectors/comment-selector.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	ContextualAnalyticsData,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useUserPreferredWorkItemLayout } from '@atlassian/jira-issue-view-layout/src/services/main.tsx';
import { AI_COMMENTS_FEATURE } from '../../common/constants.tsx';
import { CannedCommentsPlaceholder } from '../../common/ui/canned-comment-placeholder/index.tsx';
import { CommentButtonGroup } from '../../common/ui/comment-button-group/index.tsx';
import {
	useAICommentsStates,
	JiraIssueAICommentsContainer,
} from '../../controllers/use-ai-comments-context/index.tsx';
import { useAICommentsInteraction } from '../../controllers/use-ai-comments-interaction/index.tsx';
import AIGeneratedComment from './ai-generated-comment/index.tsx';

type CommentEditorWithAIProps = {
	onChange?: (value: ADF, source?: CommentEditorSourceTypes) => void;
	onExpanded?: () => void;
	placeholder?: string;
	isAIRepliesEnabled?: boolean;
};

export const CommentEditorWithAI = ({
	onChange,
	onExpanded,
	placeholder,
}: CommentEditorWithAIProps): React.JSX.Element => {
	const [
		{ currentCommentType, showGeneratedComment, isLoading },
		{ acceptSuggestedComment, setShowGeneratedComment, setCurrentCommentType },
	] = useAICommentsStates();
	const { userPreferredLayout } = useUserPreferredWorkItemLayout();
	const { trackAIResultAction, trackAIInteractionDismiss } = useAIEventsInstrumentation();

	const { handleCommentButtonClick } = useAICommentsInteraction();

	const onDismiss = useCallback(
		(isDiscard: boolean) => {
			trackAIInteractionDismiss({
				attributes: {
					aiExperienceName: currentCommentType ?? undefined,
					dismissedAction: isDiscard ? 'discardButtonClicked' : 'cancelButtonClicked',
				},
			});
			setShowGeneratedComment(false);
			setCurrentCommentType(null);
			onExpanded?.();
		},
		[
			trackAIInteractionDismiss,
			currentCommentType,
			setShowGeneratedComment,
			setCurrentCommentType,
			onExpanded,
		],
	);

	const onInsertComment = useCallback(
		(comment: ADF) => {
			trackAIResultAction('insert', {
				attributes: {
					aiExperienceName: currentCommentType ?? undefined,
				},
			});
			setShowGeneratedComment(false);
			currentCommentType && acceptSuggestedComment(currentCommentType);
			onExpanded?.();
			onChange?.(comment, 'ai-smart-reply');
		},
		[
			trackAIResultAction,
			currentCommentType,
			setShowGeneratedComment,
			acceptSuggestedComment,
			onExpanded,
			onChange,
		],
	);

	return (
		<AIContainer
			shouldFitContainer
			spacing="none"
			testId="canned-comments.ui.ai-comments.ai-container"
			isLoading={isLoading && showGeneratedComment}
			hasNewBorderExperience
			// The breadcrumbs have a z-index of 99; the AI container needs a lower z-index to prevent overlap.
			zIndex={userPreferredLayout === 'WIDE' ? 98 : undefined}
		>
			{showGeneratedComment && (
				<AIGeneratedComment
					isLoading={isLoading}
					onDismiss={onDismiss}
					onInsertComment={onInsertComment}
				/>
			)}
			{!showGeneratedComment && (
				<CannedCommentsPlaceholder
					onClick={onExpanded}
					onFocus={onExpanded}
					placeholder={placeholder}
					hiddenBorder
					renderCannedCommentOptions={() => (
						<CommentButtonGroup onButtonClick={handleCommentButtonClick} />
					)}
					isAIRepliesEnabled
				/>
			)}
		</AIContainer>
	);
};

export const CommentEditorWithAIContext = ({ children }: { children: ReactNode }) => {
	const singleInstrumentationId = useRef(uuid());
	const attributes = {
		aiFeatureName: AI_COMMENTS_FEATURE,
	};
	const issueKey = useIssueKey();
	return (
		<ReduxConnectedJiraIssueAICommentsContainer scope={issueKey}>
			<ContextualAnalyticsData attributes={attributes}>
				<AIEventsInstrumentationProvider
					config={{
						...attributes,
						product: 'jira',
						subproduct: 'jira',
						proactiveGeneratedAI: 0,
						userGeneratedAI: 1,
						isAIFeature: 1,
						singleInstrumentationID: singleInstrumentationId.current,
						customAnalyticsFire: (payload, event) => fireTrackAnalytics(event, payload),
					}}
				>
					{children}
				</AIEventsInstrumentationProvider>
			</ContextualAnalyticsData>
		</ReduxConnectedJiraIssueAICommentsContainer>
	);
};

const ReduxConnectedJiraIssueAICommentsContainerInner = connect(
	(state, ownProps: { shouldUseRelayIssueComments: boolean }) => {
		const totalOfComments = totalCommentsSelector(state);
		const relayBridgeHasComments = relayBridgeHasCommentsSelector(state);

		const hasComments =
			ownProps.shouldUseRelayIssueComments && fg('relay-migration-comment-count')
				? relayBridgeHasComments
				: totalOfComments > 0;

		return {
			totalOfComments,
			hasComments,
			hasMissingAssignee: fieldSelector(ASSIGNEE)(state)?.value === null,
			hasMissingDescription: fieldSelector(DESCRIPTION)(state)?.adfValue?.content.length === 0,
		};
	},
	{},
)(JiraIssueAICommentsContainer);

interface WrapperProps {
	children?: React.ReactNode;
	[key: string]: unknown;
}

const ReduxConnectedJiraIssueAICommentsContainer = ({ children, ...props }: WrapperProps) => {
	const shouldUseRelayIssueComments = useShouldUseRelayIssueComments();
	return (
		<ReduxConnectedJiraIssueAICommentsContainerInner
			shouldUseRelayIssueComments={shouldUseRelayIssueComments}
			{...props}
		>
			{children}
		</ReduxConnectedJiraIssueAICommentsContainerInner>
	);
};

const CommentEditorWithAIExports = {
	CommentEditorWithAI,
	CommentEditorWithAIContext,
};

export default CommentEditorWithAIExports;
